import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDrawer } from '../context/DrawerContext';
import { useConfig } from '../context/ConfigContext';
import { Icon, IconButton, MemaLogo, Badge, Avatar } from './ui';

export const MENU_PROF = [
  { group: 'Principal', items: [
    { id: 'home',      icon: 'home',       label: 'Início',          path: '/home' },
    { id: 'aulas',     icon: 'calendar',   label: 'Agenda',          path: '/aulas' },
    { id: 'conteudos', icon: 'book',       label: 'Conteúdos',       path: '/conteudos' },
  ]},
  { group: 'Gestão', items: [
    { id: 'alunos',    icon: 'users',      label: 'Alunos',          path: '/alunos' },
    { id: 'aprovar',   icon: 'user-check', label: 'Aprovar alunos',  path: '/aprovar-alunos' },
    { id: 'usuarios',  icon: 'user',       label: 'Usuários',        path: '/usuarios' },
    { id: 'financeiro',icon: 'payment',    label: 'Financeiro',      path: '/financeiro' },
    { id: 'pagamentos',icon: 'payment',    label: 'Pagamentos',      path: '/pagamentos' },
    { id: 'relatorio', icon: 'chart',      label: 'Relatório mensal',path: '/relatorio' },
    { id: 'horarios',  icon: 'clock',      label: 'Horários abertos',path: '/horarios-disponiveis' },
    { id: 'config',    icon: 'settings',   label: 'Configurações',   path: '/configuracoes' },
  ]},
  { group: 'Conta', items: [
    { id: 'perfil',    icon: 'user',       label: 'Meu perfil',      path: '/meu-perfil' },
  ]},
];

export const MENU_ALUNO = [
  { group: 'Principal', items: [
    { id: 'home',      icon: 'home',     label: 'Início',     path: '/home' },
    { id: 'aulas',     icon: 'calendar', label: 'Minhas aulas',path: '/aulas' },
    { id: 'conteudos', icon: 'book',     label: 'Materiais',  path: '/conteudos' },
  ]},
  { group: 'Conta', items: [
    { id: 'perfil',    icon: 'user',    label: 'Meu perfil',    path: '/meu-perfil' },
  ]},
];

export default function MenuDrawer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tipo, nome, fotoUrl, logout } = useAuth();
  const { open, setOpen } = useDrawer();
  const { config } = useConfig();

  if (!tipo || !open) return null;

  const isAluno = tipo === 'aluno';
  const groups = isAluno ? MENU_ALUNO : MENU_PROF;
  const displayName = nome || (isAluno ? 'Aluno' : 'Professora');
  const sub = isAluno ? 'Aluno' : 'Professora';

  function handleNav(path) {
    setOpen(false);
    navigate(path);
  }

  function handleLogout() {
    setOpen(false);
    logout();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', pointerEvents: 'auto',
    }}>
      {/* Drawer panel */}
      <div style={{
        width: '88%', maxWidth: 320, height: '100%',
        background: 'var(--bg-surface)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideIn .25s cubic-bezier(.22,1,.36,1)',
        borderRight: '1px solid var(--border-hairline)',
        boxShadow: 'var(--shadow-lg)',
      }}>
        {/* Header */}
        <div style={{
          padding: 'var(--gap-3)',
          display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
          borderBottom: '1px solid var(--border-hairline)',
        }}>
          {config.logo_url
            ? <img src={config.logo_url} alt="Logo" style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', objectFit: 'contain', flexShrink: 0 }}/>
            : <MemaLogo size={32}/>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: -0.2, color: 'var(--text-primary)' }}>{config.nome_sistema || 'Aulas da Mema'}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>gestão de aulas</div>
          </div>
          <IconButton icon="close" onClick={() => setOpen(false)} variant="ghost"/>
        </div>

        {/* Groups */}
        <div className="mema-scroll" style={{ flex: 1, overflow: 'auto', padding: '0 var(--gap-2) var(--gap-3)' }}>
          {groups.map((g, gi) => (
            <div key={gi} style={{ marginTop: 'var(--gap-2)' }}>
              <div style={{
                padding: '8px var(--gap-2) 4px',
                fontSize: 10, fontWeight: 600, letterSpacing: 0.6,
                color: 'var(--text-tertiary)', textTransform: 'uppercase',
              }}>{g.group}</div>
              {g.items.map((it) => {
                const active = location.pathname === it.path;
                return (
                  <button key={it.id}
                    onClick={() => handleNav(it.path)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
                      width: '100%', height: 36, padding: '0 8px',
                      border: 'none',
                      background: active ? 'var(--bg-hover)' : 'transparent',
                      color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                      borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 13.5, fontWeight: active ? 540 : 500,
                      letterSpacing: -0.1, transition: 'background .12s, color .12s',
                    }}>
                    <Icon name={it.icon} size={16} style={{
                      color: active ? 'var(--accent)' : 'var(--text-tertiary)',
                    }}/>
                    <span style={{ flex: 1, textAlign: 'left' }}>{it.label}</span>
                    {it.badge && <Badge tone="accent">{it.badge}</Badge>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: 'var(--gap-3)',
          borderTop: '1px solid var(--border-hairline)',
          display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
        }}>
          {fotoUrl
            ? <img src={fotoUrl} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>
            : <Avatar name={displayName} size={36}/>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 540, color: 'var(--text-primary)',
              whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{displayName}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{sub}</div>
          </div>
          <button onClick={handleLogout} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, borderRadius: 'var(--radius-md)',
            cursor: 'pointer', border: 'none', background: 'transparent',
            color: 'var(--danger)',
          }}>
            <Icon name="logout" size={16}/>
          </button>
        </div>
      </div>

      {/* Scrim */}
      <div onClick={() => setOpen(false)} style={{
        flex: 1, background: 'rgba(0,0,0,0.35)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        animation: 'fadeIn .2s ease',
      }}/>
    </div>
  );
}
